import React, { useState } from 'react';
import { Logo, Button, Icon } from '../components/UI';
import { useApp } from '../context/AppContext';

export default function LoginPage() {
  const { login, signUp, requestPasswordReset } = useApp();
  const [mode, setMode] = useState('signin'); // signin | signup | forgot
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);

  const switchMode = (next) => {
    setMode(next);
    setError('');
    setInfo('');
  };

  const handleLogin = async () => {
    if (!email || !password) { setError('Please enter your email and password.'); return; }
    setLoading(true);
    setError('');
    try {
      await login(email, password);
    } catch {
      setError('Invalid email or password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async () => {
    if (!name.trim() || !email || !password) { setError('Please fill in your name, email, and password.'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    setLoading(true);
    setError('');
    try {
      const { needsEmailConfirmation } = await signUp(name, email, password);
      if (needsEmailConfirmation) {
        setInfo("If that's a new email address, check your inbox for a confirmation link. Already have an account? Just sign in below.");
        setMode('signin');
        setPassword('');
      }
    } catch (err) {
      setError(err.message === 'User already registered' ? 'An account with this email already exists.' : 'Could not create your account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) { setError('Enter your email address above first, then click "Forgot password?".'); return; }
    setLoading(true);
    setError('');
    try {
      await requestPasswordReset(email);
      setInfo('Check your email for a link to reset your password.');
    } catch {
      setError('Could not send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => (mode === 'signin' ? handleLogin() : handleSignUp());

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bone)', display: 'flex', fontFamily: 'var(--font-body)' }}>
      {/* Left panel */}
      <div style={{ flex: '0 0 420px', background: 'var(--dark)', display: 'flex', flexDirection: 'column', padding: '48px 44px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: -60, right: -60, width: 280, height: 280, borderRadius: '50%', background: 'rgba(236,125,42,0.12)' }} />
        <div style={{ position: 'absolute', bottom: 40, left: -40, width: 180, height: 180, borderRadius: '50%', background: 'rgba(236,125,42,0.07)' }} />

        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ marginBottom: 56 }}>
            <Logo size="lg" variant="white" />
          </div>

          <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: 32, fontWeight: 800, color: 'white', lineHeight: 1.2, margin: '0 0 16px', letterSpacing: '-0.02em' }}>
            Your learning,<br />all in one place.
          </h2>
          <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.5)', lineHeight: 1.7, margin: '0 0 48px' }}>
            Access your programmes, courses, and resources — all behind a single sign-in.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {[
              { icon: 'book', text: 'E-Waste, Skills, SMME & TCS IoT programmes' },
              { icon: 'users', text: 'Student & admin portals' },
              { icon: 'mail', text: 'Direct messaging from coordinators' },
              { icon: 'external', text: 'One-click access to Cisco, Google Classroom & more' },
            ].map(item => (
              <div key={item.text} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(236,125,42,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                  <Icon name={item.icon} size={15} color="var(--orange)" />
                </div>
                <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', lineHeight: 1.5, margin: 0 }}>{item.text}</p>
              </div>
            ))}
          </div>
        </div>

        <div style={{ marginTop: 'auto', position: 'relative', zIndex: 1 }}>
          <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.25)' }}>Algorhythmlab Pty Ltd · © 2026</p>
        </div>
      </div>

      {/* Right panel */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40 }}>
        <div style={{ width: '100%', maxWidth: 400 }}>
          <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: 26, fontWeight: 800, color: 'var(--dark)', margin: '0 0 6px', letterSpacing: '-0.02em' }}>
            {mode === 'signin' ? 'Sign in' : 'Create your account'}
          </h1>
          <p style={{ fontSize: 14, color: 'var(--dark-60)', margin: '0 0 32px' }}>
            {mode === 'signin' ? 'Enter your credentials to access the hub.' : 'Sign up as a student — an administrator will assign you to programmes.'}
          </p>

          {info && (
            <div style={{ background: 'var(--success-soft)', border: '1px solid rgba(46,139,87,0.2)', borderRadius: 'var(--radius-md)', padding: '10px 14px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Icon name="check" size={15} color="var(--success)" />
              <p style={{ color: 'var(--success)', fontSize: 13, margin: 0 }}>{info}</p>
            </div>
          )}

          {mode === 'signup' && (
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontFamily: 'var(--font-heading)', fontSize: 13, fontWeight: 600, color: 'var(--dark-80)', marginBottom: 6 }}>Full name</label>
              <input
                type="text" value={name} onChange={e => setName(e.target.value)}
                placeholder="Your name"
                onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                style={{ width: '100%', padding: '11px 14px', border: '1.5px solid var(--dark-20)', borderRadius: 'var(--radius-md)', fontSize: 14, outline: 'none', background: 'white' }}
                onFocus={e => e.target.style.borderColor = 'var(--orange)'}
                onBlur={e => e.target.style.borderColor = 'var(--dark-20)'}
              />
            </div>
          )}

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontFamily: 'var(--font-heading)', fontSize: 13, fontWeight: 600, color: 'var(--dark-80)', marginBottom: 6 }}>Email address</label>
            <input
              type="email" value={email} onChange={e => setEmail(e.target.value)}
              placeholder="your@email.com"
              onKeyDown={e => e.key === 'Enter' && handleSubmit()}
              style={{ width: '100%', padding: '11px 14px', border: '1.5px solid var(--dark-20)', borderRadius: 'var(--radius-md)', fontSize: 14, outline: 'none', background: 'white' }}
              onFocus={e => e.target.style.borderColor = 'var(--orange)'}
              onBlur={e => e.target.style.borderColor = 'var(--dark-20)'}
            />
          </div>

          <div style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <label style={{ display: 'block', fontFamily: 'var(--font-heading)', fontSize: 13, fontWeight: 600, color: 'var(--dark-80)' }}>Password</label>
              {mode === 'signin' && (
                <button onClick={handleForgotPassword} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--orange)', fontSize: 12, fontWeight: 600, fontFamily: 'var(--font-heading)' }}>Forgot password?</button>
              )}
            </div>
            <div style={{ position: 'relative' }}>
              <input
                type={showPass ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                placeholder={mode === 'signin' ? 'Enter your password' : 'At least 6 characters'}
                onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                style={{ width: '100%', padding: '11px 44px 11px 14px', border: '1.5px solid var(--dark-20)', borderRadius: 'var(--radius-md)', fontSize: 14, outline: 'none', background: 'white' }}
                onFocus={e => e.target.style.borderColor = 'var(--orange)'}
                onBlur={e => e.target.style.borderColor = 'var(--dark-20)'}
              />
              <button onClick={() => setShowPass(s => !s)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--dark-40)', padding: 0 }}>
                <Icon name="eye" size={16} />
              </button>
            </div>
          </div>

          {error && (
            <div style={{ background: 'var(--danger-soft)', border: '1px solid rgba(214,69,69,0.2)', borderRadius: 'var(--radius-md)', padding: '10px 14px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Icon name="alert" size={15} color="var(--danger)" />
              <p style={{ color: 'var(--danger)', fontSize: 13, margin: 0 }}>{error}</p>
            </div>
          )}

          <Button onClick={handleSubmit} fullWidth disabled={loading} size="lg" style={{ justifyContent: 'center' }}>
            {loading ? (mode === 'signin' ? 'Signing in…' : 'Creating account…') : (mode === 'signin' ? 'Sign in' : 'Sign up')}
          </Button>

          <p style={{ fontSize: 13, color: 'var(--dark-60)', textAlign: 'center', margin: '20px 0 0' }}>
            {mode === 'signin' ? (
              <>Don't have an account?{' '}
                <button onClick={() => switchMode('signup')} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--orange)', fontWeight: 600, fontFamily: 'var(--font-heading)', fontSize: 13 }}>Sign up</button>
              </>
            ) : (
              <>Already have an account?{' '}
                <button onClick={() => switchMode('signin')} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--orange)', fontWeight: 600, fontFamily: 'var(--font-heading)', fontSize: 13 }}>Sign in</button>
              </>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
